import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  accountGhostButtonClassName,
  accountPrimaryButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';

const NONE_VALUE = '__none__';

type Option = { value: string; labelKey: string; defaultLabel: string };

const SALUTATION_OPTIONS: Option[] = [
  { value: 'Mr', labelKey: 'ACCOUNT_SALUTATION_MR', defaultLabel: 'Mr' },
  { value: 'Mrs', labelKey: 'ACCOUNT_SALUTATION_MRS', defaultLabel: 'Mrs' },
  { value: 'Ms', labelKey: 'ACCOUNT_SALUTATION_MS', defaultLabel: 'Ms' },
  { value: 'Mx', labelKey: 'ACCOUNT_SALUTATION_MX', defaultLabel: 'Mx' },
  { value: 'Dr', labelKey: 'ACCOUNT_SALUTATION_DR', defaultLabel: 'Dr' },
];

// Industry / Area have no account API yet — shown for the Figma layout only.
const INDUSTRY_OPTIONS: Option[] = [
  { value: 'technology', labelKey: 'ACCOUNT_INDUSTRY_TECHNOLOGY', defaultLabel: 'Technology' },
  { value: 'finance', labelKey: 'ACCOUNT_INDUSTRY_FINANCE', defaultLabel: 'Finance' },
  { value: 'healthcare', labelKey: 'ACCOUNT_INDUSTRY_HEALTHCARE', defaultLabel: 'Healthcare' },
  { value: 'education', labelKey: 'ACCOUNT_INDUSTRY_EDUCATION', defaultLabel: 'Education' },
  { value: 'other', labelKey: 'ACCOUNT_INDUSTRY_OTHER', defaultLabel: 'Other' },
];

const AREA_OPTIONS: Option[] = [
  { value: 'engineering', labelKey: 'ACCOUNT_AREA_ENGINEERING', defaultLabel: 'Engineering' },
  { value: 'product', labelKey: 'ACCOUNT_AREA_PRODUCT', defaultLabel: 'Product' },
  { value: 'operations', labelKey: 'ACCOUNT_AREA_OPERATIONS', defaultLabel: 'Operations' },
  { value: 'sales', labelKey: 'ACCOUNT_AREA_SALES', defaultLabel: 'Sales' },
  { value: 'other', labelKey: 'ACCOUNT_AREA_OTHER', defaultLabel: 'Other' },
];

export type PersonalCardChanges = {
  salutation: string;
};

interface PersonalCardProps {
  salutation: string;
  isSaving: boolean;
  onSave: (changes: PersonalCardChanges) => void;
}

interface FieldLabelProps {
  htmlFor?: string;
  children: ReactNode;
  titleColor: string;
}

function FieldLabel({ htmlFor, children, titleColor }: FieldLabelProps) {
  return (
    <label htmlFor={htmlFor} className={cn('text-sm font-medium leading-body', titleColor)}>
      {children}
    </label>
  );
}

interface OptionalSelectProps {
  label: string;
  value: string;
  options: Option[];
  disabled: boolean;
  onChange: (value: string) => void;
  titleColor: string;
}

function OptionalSelect({
  label,
  value,
  options,
  disabled,
  onChange,
  titleColor,
}: OptionalSelectProps) {
  const { t } = useTranslation();
  const selectValue = value || NONE_VALUE;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <FieldLabel titleColor={titleColor}>{label}</FieldLabel>
      <Select
        value={selectValue}
        disabled={disabled}
        onValueChange={(next) => onChange(next === NONE_VALUE ? '' : next)}
      >
        <SelectTrigger
          aria-label={label}
          className="h-10 w-full justify-between rounded-xl border-grey-100 bg-true-white px-3 text-sm text-grey-950 shadow-none focus:ring-0 dark:border-border dark:bg-card dark:text-foreground"
        >
          <SelectValue placeholder=" " />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>
            <span className="text-grey-500">
              {t('ACCOUNT_SELECT_NONE', { defaultValue: 'None' })}
            </span>
          </SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {t(option.labelKey, { defaultValue: option.defaultLabel })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Personal card (Figma Account) — salutation saves via the account API; Industry and Area are UI-only for now. */
export function PersonalCard({ salutation, isSaving, onSave }: PersonalCardProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [draftSalutation, setDraftSalutation] = useState(salutation);
  const [industry, setIndustry] = useState('');
  const [area, setArea] = useState('');

  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const mutedColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-500';

  useEffect(() => {
    setDraftSalutation(salutation);
  }, [salutation]);

  const hasApiChanges = draftSalutation !== salutation;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasApiChanges) return;
    onSave({ salutation: draftSalutation });
  };

  const handleCancel = () => {
    setDraftSalutation(salutation);
    setIndustry('');
    setArea('');
  };

  return (
    <form onSubmit={handleSubmit} className={accountSectionClassName} data-testid="personal-card">
      <div className="flex flex-col gap-4 px-6 pb-5 pt-5">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className={accountSectionTitleClassName}>
            {t('ACCOUNT_PERSONAL', { defaultValue: 'Personal' })}
          </h2>
          <p className={cn('text-sm leading-body', mutedColor)}>
            {t('ACCOUNT_PERSONAL_HINT', {
              defaultValue: 'So Buddy and your agents address you the right way.',
            })}
          </p>
        </div>

        {/* Two per row from sm up; one column on phones. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <OptionalSelect
            label={t('ACCOUNT_SALUTATION', { defaultValue: 'Salutation' })}
            value={draftSalutation}
            options={SALUTATION_OPTIONS}
            disabled={isSaving}
            onChange={setDraftSalutation}
            titleColor={titleColor}
          />

          {/* Industry and Area: Figma UI only — no account API fields yet. */}
          <OptionalSelect
            label={t('ACCOUNT_INDUSTRY', { defaultValue: 'Industry' })}
            value={industry}
            options={INDUSTRY_OPTIONS}
            disabled={isSaving}
            onChange={setIndustry}
            titleColor={titleColor}
          />
          <OptionalSelect
            label={t('ACCOUNT_AREA', { defaultValue: 'Area' })}
            value={area}
            options={AREA_OPTIONS}
            disabled={isSaving}
            onChange={setArea}
            titleColor={titleColor}
          />
        </div>
      </div>

      {hasApiChanges ? (
        <div className="flex items-center justify-end gap-2 border-t border-grey-100 px-6 py-4 dark:border-border">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className={cn(accountGhostButtonClassName, 'px-4')}
          >
            {t('ACCOUNT_CANCEL', { defaultValue: 'Cancel' })}
          </button>
          <button type="submit" disabled={isSaving} className={accountPrimaryButtonClassName}>
            {t('ACCOUNT_SAVE_CHANGES', { defaultValue: 'Save changes' })}
          </button>
        </div>
      ) : null}
    </form>
  );
}
